import { Mail } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { ChannelApple } from '@/app/components/icons/ChannelApple';
import { ChannelGoogle } from '@/app/components/icons/ChannelGoogle';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/use-toast';
import { reportEvent } from '@/app/utils';
import { hysdk } from '@/app/utils/client';
import type { CardType, LoginType, ThirdPartyLoginAppType } from '../../types';
import { InviteCodeInput } from './InviteCodeInput';
import { LoginSeparator } from './LoginSeparator';

export const LoginOptions = ({
  handleClickThirdPartyLogin,
  handleClickLoginNext,
  loading,
  handleValidateInviteCode,
  inviteCodeError,
  inviteCode,
  showInviteCode = true,
}: {
  handleClickThirdPartyLogin: (type: ThirdPartyLoginAppType) => void;
  handleClickLoginNext: (type: CardType) => void;
  loading: boolean;
  handleValidateInviteCode: (inviteCode: string) => void;
  inviteCodeError: string;
  inviteCode: string;
  showInviteCode?: boolean;
}) => {
  const t = useTranslations();

  const handleClickLogin = (type: LoginType) => {
    if (type === 'google' && hysdk.sniff.isWechat) {
      toast({
        type: 'warning',
        message: t(`login.${type}LoginWarning`),
      });
      return;
    }

    if (type === 'google' || type === 'apple') {
      handleClickThirdPartyLogin(type);
    } else {
      handleClickLoginNext(type.toUpperCase() as CardType);
    }

    reportEvent('choose_login_method', { login_method: type });
  };

  return (
    <div className="flex w-full max-w-[350px] flex-col items-center gap-5 px-4 pt-10 lg:gap-6 lg:px-0 lg:pt-6">
      <Button
        disabled={loading}
        loading={loading}
        onClick={() => handleClickLogin('google')}
        className="h-12 w-full rounded-12 border-none bg-white font-medium text-base text-black disabled:text-black disabled:opacity-50"
      >
        <ChannelGoogle className="mr-2 size-5" />
        {t('login.chromeLogin')}
      </Button>
      <Button
        disabled={loading}
        loading={loading}
        onClick={() => handleClickLogin('apple')}
        className="h-12 w-full rounded-12 border-none bg-white font-medium text-base text-black disabled:text-black disabled:opacity-50"
      >
        <ChannelApple className="mr-2 size-5" />
        {t('login.appleLogin')}
      </Button>
      <Button
        variant="secondary"
        disabled={loading}
        loading={loading}
        onClick={() => handleClickLogin('email')}
        className="h-12 w-full rounded-12 border-none bg-white font-medium text-base text-black disabled:text-black disabled:opacity-50"
      >
        <Mail className="mr-2 size-5" />
        {t('login.emailLogin')}
      </Button>

      {showInviteCode && (
        <>
          <LoginSeparator />

          <InviteCodeInput
            handleValidateInviteCode={handleValidateInviteCode}
            inviteCodeError={inviteCodeError}
            inviteCode={inviteCode}
          />
        </>
      )}
    </div>
  );
};
